import { api } from './apiClient';

/**
 * Project governance entities client (migration 061): milestones, risks (+matrix),
 * issues, change requests, billing milestones, documents. Under /project-extras.
 * Rows are snake_case off SELECT; create/update bodies are camelCase.
 */

export const MILESTONE_STATUSES = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'AT_RISK', 'DELAYED'] as const;
export const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH'] as const;
export const RISK_STATUSES = ['OPEN', 'MITIGATING', 'CLOSED', 'ACCEPTED'] as const;
export const ISSUE_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export const ISSUE_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const;
export const CR_STATUSES = ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'IMPLEMENTED'] as const;
export const BILLING_STATUSES = ['PENDING', 'READY', 'BILLED', 'COLLECTED', 'CANCELLED'] as const;

export const projectExtrasService = {
  // Milestones
  listMilestones: (projectId: number): Promise<{ milestones: any[] }> =>
    api.get(`/project-extras/projects/${projectId}/milestones`),
  createMilestone: (projectId: number, data: any): Promise<{ milestone: any }> =>
    api.post(`/project-extras/projects/${projectId}/milestones`, data),
  updateMilestone: (id: number, data: any): Promise<{ milestone: any }> =>
    api.put(`/project-extras/milestones/${id}`, data),
  setMilestoneStatus: (id: number, status: string): Promise<{ milestone: any }> =>
    api.patch(`/project-extras/milestones/${id}/status`, { status }),
  deleteMilestone: (id: number): Promise<void> =>
    api.delete(`/project-extras/milestones/${id}`).then(() => undefined),

  // Risks
  listRisks: (projectId: number): Promise<{ risks: any[] }> =>
    api.get(`/project-extras/projects/${projectId}/risks`),
  riskMatrix: (projectId: number): Promise<any> =>
    api.get(`/project-extras/projects/${projectId}/risk-matrix`),
  createRisk: (projectId: number, data: any): Promise<{ risk: any }> =>
    api.post(`/project-extras/projects/${projectId}/risks`, data),
  updateRisk: (id: number, data: any): Promise<{ risk: any }> =>
    api.put(`/project-extras/risks/${id}`, data),
  deleteRisk: (id: number): Promise<void> =>
    api.delete(`/project-extras/risks/${id}`).then(() => undefined),

  // Issues
  listIssues: (projectId: number): Promise<{ issues: any[] }> =>
    api.get(`/project-extras/projects/${projectId}/issues`),
  createIssue: (projectId: number, data: any): Promise<{ issue: any }> =>
    api.post(`/project-extras/projects/${projectId}/issues`, data),
  updateIssue: (id: number, data: any): Promise<{ issue: any }> =>
    api.put(`/project-extras/issues/${id}`, data),
  deleteIssue: (id: number): Promise<void> =>
    api.delete(`/project-extras/issues/${id}`).then(() => undefined),

  // Change requests
  listChangeRequests: (projectId: number): Promise<{ changeRequests: any[] }> =>
    api.get(`/project-extras/projects/${projectId}/change-requests`),
  createChangeRequest: (projectId: number, data: any): Promise<{ changeRequest: any }> =>
    api.post(`/project-extras/projects/${projectId}/change-requests`, data),
  updateChangeRequest: (id: number, data: any): Promise<{ changeRequest: any }> =>
    api.put(`/project-extras/change-requests/${id}`, data),
  setChangeRequestStatus: (id: number, status: string, extra?: any): Promise<{ changeRequest: any }> =>
    api.patch(`/project-extras/change-requests/${id}/status`, { status, ...(extra || {}) }),

  // Billing milestones
  listBillingMilestones: (projectId: number): Promise<{ billingMilestones: any[] }> =>
    api.get(`/project-extras/projects/${projectId}/billing-milestones`),
  createBillingMilestone: (projectId: number, data: any): Promise<{ billingMilestone: any }> =>
    api.post(`/project-extras/projects/${projectId}/billing-milestones`, data),
  updateBillingMilestone: (id: number, data: any): Promise<{ billingMilestone: any }> =>
    api.put(`/project-extras/billing-milestones/${id}`, data),
  setBillingMilestoneStatus: (id: number, status: string, extra?: any): Promise<{ billingMilestone: any }> =>
    api.patch(`/project-extras/billing-milestones/${id}/status`, { status, ...(extra || {}) }),

  // Documents
  listDocuments: (projectId: number): Promise<{ documents: any[] }> =>
    api.get(`/project-extras/projects/${projectId}/documents`),
  createDocument: (projectId: number, data: any): Promise<{ document: any }> =>
    api.post(`/project-extras/projects/${projectId}/documents`, data),
  deleteDocument: (id: number): Promise<void> =>
    api.delete(`/project-extras/documents/${id}`).then(() => undefined),
};

export default projectExtrasService;
